import * as React from 'react';
import Stack from '@mui/material/Stack';
import MuiDialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import * as Device from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useParams } from 'react-router-dom';

import { useGetSpotDetailedQuery } from '@graphql';
import { parseApolloError } from '@utils';
import { useSession } from '@hooks';
import { DialogEditWrapper, DialogEditContext } from '../common/layout';
import SpotSkeleton from './SpotSkeleton';
import Header from './header';
import Preview from './preview';
import Agenda from './agenda';
import Rex from './rex';

interface SpotDialogProps {
  handleBack: (toRoot: boolean) => void;
}

function SpotDialog(props: Readonly<SpotDialogProps>) {
  const { account } = useSession();
  const [activeTab, setActiveTab] = React.useState(0);
  const swiperRef = React.useRef<SwiperClass>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const params = useParams();
  const { t } = useTranslation('spot');

  const padding = isMobile ? 1 : 3;

  const { data, loading, error } = useGetSpotDetailedQuery({
    skip: !searchParams.get('s') && !params.slug,
    variables: { slug: searchParams.get('s') ?? params.slug ?? '' },
    errorPolicy: 'all',
  });

  React.useEffect(() => {
    return () => {
      setActiveTab(0);
      swiperRef.current?.slideReset();
    };
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newTab: number) => {
    setActiveTab(newTab);

    if (swiperRef.current) {
      swiperRef.current.slideTo(newTab);
    }
  };

  const handleSwiperSlideChange = (swiper: any) => {
    setActiveTab(swiper.activeIndex);
  };

  if (loading) {
    return <SpotSkeleton />;
  }

  if (error) {
    import.meta.env.DEV && console.error('[SpotDialog]', parseApolloError(error));

    return (
      <Stack sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <Stack sx={{ position: 'relative' }}>
          <Header
            slug={searchParams.get('s') ?? params.slug ?? ''}
            activeTab={activeTab}
            handleBack={props.handleBack}
            handleTabChange={handleTabChange}
            disabled={true}
          />
        </Stack>
        <MuiDialogContent
          sx={{
            p: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Stack spacing={5} p={padding}>
            <Swiper
              onSlideChange={handleSwiperSlideChange}
              onSwiper={(swiper) => (swiperRef.current = swiper)}
              slidesPerView={1}
              style={{ marginTop: 0, padding: Device.isDesktop ? 10 : 5 }}
              initialSlide={activeTab}
            >
              <Typography
                variant="h6"
                align="center"
                fontSize={{ xs: '.8rem', sm: '1rem', md: '1.2rem' }}
                color="textSecondary"
              >
                {t('This spot is currently unavailable.')}
              </Typography>
            </Swiper>
          </Stack>
        </MuiDialogContent>
      </Stack>
    );
  }

  if (!data?.spot) {
    return null;
  }

  const isOwner = account?.id === data.spot.id;

  return (
    <DialogEditWrapper entity="spot" spot={data.spot} isOwner={!!isOwner}>
      <DialogContent handleBack={() => props.handleBack(false)} />
    </DialogEditWrapper>
  );
}

interface DialogContentProps extends SpotDialogProps {}

function DialogContent(props: DialogContentProps) {
  const context = React.useContext(DialogEditContext);

  if (!context) {
    throw new Error('DialogContent must be used inside DialogEditWrapper');
  }

  const { spot } = context;
  const [activeTab, setActiveTab] = React.useState(0);
  const swiperRef = React.useRef<SwiperClass>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const padding = isMobile ? 1 : 3;

  React.useEffect(() => {
    return () => {
      setActiveTab(0);
      swiperRef.current?.slideReset();
    };
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newTab: number) => {
    setActiveTab(newTab);

    if (swiperRef.current) {
      swiperRef.current.slideTo(newTab);
    }
  };

  const handleSwiperSlideChange = (swiper: any) => {
    setActiveTab(swiper.activeIndex);
  };

  if (!spot) {
    return null;
  }

  return (
    <Stack sx={{ position: 'relative', width: '100%' }}>
      <Stack sx={{ position: 'relative' }}>
        <Header
          slug={spot.slug}
          covers={spot.covers}
          configuration={spot.configuration}
          activeTab={activeTab}
          handleBack={props.handleBack}
          handleTabChange={handleTabChange}
        />
      </Stack>
      <MuiDialogContent sx={{ p: 0, width: '100%' }}>
        <Stack spacing={5} p={padding}>
          <Swiper
            onSlideChange={handleSwiperSlideChange}
            onSwiper={(swiper) => (swiperRef.current = swiper)}
            slidesPerView={1}
            style={{ marginTop: 0, padding: Device.isDesktop ? 10 : 5 }}
            initialSlide={activeTab}
          >
            <SwiperSlide>{activeTab === 0 && <Preview spot={spot} />}</SwiperSlide>
            <SwiperSlide>{activeTab === 1 && <Agenda slug={spot.slug} />}</SwiperSlide>
            {/* <SwiperSlide>{activeTab === 1 && <>Menu</>}</SwiperSlide> */}
            {/* <SwiperSlide>{activeTab === 3 && <Rex spot={data.spot} />}</SwiperSlide> */}
          </Swiper>
        </Stack>
      </MuiDialogContent>
    </Stack>
  );
}

export default React.memo(SpotDialog);
